import { stringify } from 'querystring';
import parseLinkHeader from 'parse-link-header';

import { ASSERT_OK, ASSERT_STATUS_CODE, AUTHENTICATION_ERROR } from './constants';
import { BaseError, reloadHandler, redirectHandler } from './errors';

export interface Params {
    [key: string]: any;
}

const LINK_HEADER_REL = 'rel="auth"';

export class AuthenticationError extends BaseError {
    code = AUTHENTICATION_ERROR;
    statusCode: number;
    redirectTarget?: string;

    constructor(statusCode: number, linkHeader: string | null, ...rest: any[]) {
        super(...rest);

        this.statusCode = statusCode;

        if (!linkHeader) {
            return;
        }

        const links = parseLinkHeader(
            linkHeader.indexOf(LINK_HEADER_REL) === -1
                ? `${linkHeader};${LINK_HEADER_REL}`
                : linkHeader,
        );

        if (!links) {
            return;
        }

        this.redirectTarget = links.auth.url;
    }
}

const dummy = (response: Response) => response;

export const assertAndHandle40xLink = (response: Response) => {
    try {
        if (response.status !== 401 && response.status !== 403) {
            return response;
        }

        throw new AuthenticationError(response.status, response.headers.get('Link'));
    } catch (err) {
        if (err instanceof AuthenticationError && err.redirectTarget) {
            redirectHandler(err.redirectTarget);
        } else {
            reloadHandler();
        }

        throw err;
    }
};

export const createRestClient = (baseUrl: string, handle40x = true) => ({
    get(path: string, query: Params = {}): Promise<Response> {
        const queryParams = stringify(query);

        return fetch(`${baseUrl}${path}${queryParams !== '' ? `?${queryParams}` : ''}`, {
            credentials: 'same-origin',
            headers: {
                'X-Requested-With': 'XMLHttpRequest',
            },
        }).then(handle40x ? assertAndHandle40xLink : dummy);
    },
    post(path: string, query: Params = {}, payload: Params = {}): Promise<Response> {
        const queryParams = stringify(query);

        return fetch(`${baseUrl}${path}${queryParams !== '' ? `?${queryParams}` : ''}`, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify(payload),
        }).then(handle40x ? assertAndHandle40xLink : dummy);
    },
    put(path: string, query: Params = {}, payload: Params = {}): Promise<Response> {
        const queryParams = stringify(query);

        return fetch(`${baseUrl}${path}${queryParams !== '' ? `?${queryParams}` : ''}`, {
            method: 'PUT',
            credentials: 'same-origin',
            headers: {
                'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify(payload),
        }).then(handle40x ? assertAndHandle40xLink : dummy);
    },
    delete(path: string, query: Params = {}): Promise<Response> {
        const queryParams = stringify(query);

        return fetch(`${baseUrl}${path}${queryParams !== '' ? `?${queryParams}` : ''}`, {
            method: 'DELETE',
            credentials: 'same-origin',
            headers: {
                'X-Requested-With': 'XMLHttpRequest',
            },
        }).then(handle40x ? assertAndHandle40xLink : dummy);
    },
});

export type RestClient = ReturnType<typeof createRestClient>;

export class AssertOkError extends BaseError {
    code = ASSERT_OK;
}

export const assertOk = () => (response: Response): Response => {
    if (!response.ok) {
        throw new AssertOkError('Request was unsuccessful');
    }

    return response;
};

export class AssertStatusCode extends BaseError {
    code = ASSERT_STATUS_CODE;
    statusCode: number;

    constructor(statusCode: number, ...rest: any[]) {
        super(...rest);
        this.statusCode = statusCode;
    }
}

export const assertStatusCode = (codes: number[]) => (response: Response): Response => {
    if (codes.indexOf(response.status) === -1) {
        throw new AssertStatusCode(
            response.status,
            `Expected status codes ${JSON.stringify(codes)} but got ${response.status}`,
        );
    }

    return response;
};
